import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, InputNumber, message, Row, Col } from 'antd';
import api from '../../../utils/api';

const { Title, Paragraph } = Typography;

const AssignTransferClients = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [targetAgencyId, setTargetAgencyId] = useState();

  const loadClients = async () => {
    setLoading(true);
    try {
      // Basic list; backend supports filtering and pagination per memory
      const data = await api.get('/clients');
      // Expect either an array or { data, total }. Normalize to array for table.
      const list = Array.isArray(data) ? data : (data?.data || []);
      setClients(list);
    } catch (e) {
      message.error(e.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 90 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Agency ID', dataIndex: 'agency_id', key: 'agency_id', width: 120 },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120 },
  ];

  const hasSelection = selectedRowKeys.length > 0;

  const doTransfer = async () => {
    if (!targetAgencyId) {
      message.warning('Select a target agency ID');
      return;
    }
    if (!hasSelection) {
      message.warning('Select at least one client');
      return;
    }
    setSaving(true);
    try {
      // Batch: sequential PUTs to update agency_id per client
      for (const id of selectedRowKeys) {
        await api.put(`/clients/${id}`, { agency_id: targetAgencyId });
      }
      message.success('Clients transferred');
      setSelectedRowKeys([]);
      loadClients();
    } catch (e) {
      message.error(e.message || 'Failed to transfer clients');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Clients' },
        { title: 'Assign/Transfer Clients' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Assign/Transfer Clients</Title>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Select one or more clients and set a target Agency ID to assign or transfer them. This uses the clients update API to set agency_id.
        </Paragraph>

        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col>
            <InputNumber
              placeholder="Target Agency ID"
              value={targetAgencyId}
              onChange={setTargetAgencyId}
            />
          </Col>
          <Col>
            <Button type="primary" onClick={doTransfer} loading={saving} disabled={!hasSelection}>
              Transfer to Agency
            </Button>
          </Col>
          <Col>
            <Button onClick={() => setSelectedRowKeys([])} disabled={!hasSelection}>Clear Selection</Button>
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={clients}
          loading={loading}
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
};

export default AssignTransferClients;
